import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, Camera, Receipt, LogOut, User, Settings, HelpCircle } from 'lucide-react';
import { AuthUser } from '../services/authService';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
  user: AuthUser;
  isAdmin?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({ children, onLogout, user, isAdmin = false }) => {
  const location = useLocation();

  // Desktop Navigation Items (Left Sidebar)
  const desktopNavItems = [
    { path: '/', label: 'Inicio', icon: <Home size={20} /> },
    { path: '/reports', label: 'Reportes', icon: <FileText size={20} /> },
    { path: '/invoices', label: 'Facturas', icon: <Receipt size={20} /> },
    // Note: 'Capturar' in desktop usually triggers file upload directly, handled in the page logic
    { path: '/capture', label: 'Capturar Factura', icon: <Camera size={20} /> },
    { path: '/settings', label: 'Configuración', icon: <Settings size={20} /> },

  ];

  return (
    <div className="flex min-h-screen bg-neutral-50">
      
      {/* Header (Mobile/Tablet only for title) */}
      <header className="bg-white border-b border-neutral-200 shadow-sm fixed top-0 left-0 right-0 z-20 h-16 lg:hidden">
        <div className="px-4 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#003D7F] rounded-xl flex items-center justify-center text-white font-bold shadow-sm">
              LZ
            </div>
            <h1 className="text-lg font-bold text-neutral-800 tracking-tight">Expense</h1>
          </div>
          <button 
            onClick={onLogout} 
            className="text-neutral-500 hover:text-danger transition-colors p-2"
            title="Cerrar Sesión"
            aria-label="Cerrar sesión"
          >
            <LogOut size={20} />
          </button>
        </div>
      </header>

      {/* Desktop Header/Logo Area (Part of Main Layout) */}
      <div className="hidden lg:block fixed top-0 left-64 right-0 h-16 bg-white border-b border-neutral-200 z-20 px-8">
         <div className="h-full flex items-center justify-between">
            <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#003D7F] rounded-xl flex items-center justify-center text-white font-bold shadow-sm">
                LZ
                </div>
                <h1 className="text-xl font-bold text-neutral-800 tracking-tight">Expense</h1>
            </div>
         </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 w-full pt-16 pb-24 lg:pb-8 lg:pl-72 px-4 lg:px-8 max-w-7xl mx-auto">
        <div className="mt-6">
            {children}
        </div>
      </main>

      {/* Desktop Left Sidebar Navigation */}
      <aside className="hidden lg:flex flex-col w-64 fixed left-0 top-0 bottom-0 bg-white border-r border-neutral-200 z-30">
        <div className="h-16 border-b border-neutral-200 flex items-center justify-between px-6">
             <div className="flex items-center gap-2 text-neutral-700 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                  <User size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-xs truncate max-w-[120px]">
                    {user.full_name || 'Usuario'}
                  </span>
                  <span className="text-[10px] text-neutral-500 truncate max-w-[120px]">
                    {user.email}
                  </span>
                </div>
             </div>
             <button onClick={onLogout} className="text-neutral-500 hover:text-danger transition-colors" title="Cerrar Sesión" aria-label="Cerrar sesión">
                <LogOut size={18} />
            </button>
        </div>
        <nav className="flex-1 p-6 space-y-2">
            {desktopNavItems.map((item) => {
                 const isActive = location.pathname === item.path;
                 return (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                            isActive 
                            ? 'bg-primary text-white shadow-md' 
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                    >
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                    </Link>
                 )
            })}
            {/* Independiente de `navigation` y de cualquier rol: todos los
                usuarios la necesitan por igual. Archivo estático servido
                directamente por nginx desde public/guias/ (try_files lo
                sirve antes de caer al index.html del SPA), así que es un
                <a> normal con target="_blank", no un <Link>: no pasa por
                React Router. En celular, el mismo enlace vive en
                Configuración (Settings.tsx), ya que la barra inferior no
                tiene espacio para un ícono más. */}
            <a
                href="/guias/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-neutral-600 hover:bg-neutral-100"
            >
                <HelpCircle size={20} />
                <span className="font-medium">Guía de Usuario</span>
            </a>
        </nav>
      </aside>

      {/* Mobile/Tablet Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-30 pb-safe">
        <div className="flex items-center justify-around px-4 h-16 relative">
          
          {/* Left Items */}
          <Link to="/" className={`flex flex-col items-center justify-center space-y-1 w-16 ${location.pathname === '/' ? 'text-primary' : 'text-neutral-500'}`}>
             <Home size={24} />
             <span className="text-[10px] font-medium">Inicio</span>
          </Link>
          
          <Link to="/reports" className={`flex flex-col items-center justify-center space-y-1 w-16 ${location.pathname.includes('/reports') ? 'text-primary' : 'text-neutral-500'}`}>
             <FileText size={24} />
             <span className="text-[10px] font-medium">Reportes</span>
          </Link>

          {/* Center FAB (Camera) - Absolute Positioned to be exactly center */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-6">
             <Link 
               to="/capture"
               className="w-16 h-16 bg-primary text-white rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform border-4 border-neutral-50"
             >
               <Camera size={32} />
             </Link>
          </div>

          {/* Spacer for FAB */}
          <div className="w-16"></div>

          {/* Right Items */}
          <Link to="/invoices" className={`flex flex-col items-center justify-center space-y-1 w-16 ${location.pathname === '/invoices' ? 'text-primary' : 'text-neutral-500'}`}>
             <Receipt size={24} />
             <span className="text-[10px] font-medium">Facturas</span>
          </Link>

          <Link to="/settings" className={`flex flex-col items-center justify-center space-y-1 w-16 ${location.pathname === '/settings' ? 'text-primary' : 'text-neutral-500'}`}>
             <Settings size={24} />
             <span className="text-[10px] font-medium">Config</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};